import * as React from 'react';

import { Notification } from '@coursera/coursera-ui';

import _t from 'i18n!nls/staff-management';

import type { ErrorInterface } from './store/types';

export type ErrorNotificationInterface = {
  error?: ErrorInterface;
};

/**
 * If component has an error, let the user know something is wrong and display associated error message.
 *
 * Designed for use with components that do not implement their own error handling.
 * Does not pass on `error` prop.
 */
export const withErrorNotification =
  <P extends {}>() =>
  (WrappedComponent: React.ComponentType<P>): React.ComponentType<P & ErrorNotificationInterface> => {
    return class Wrapper extends React.Component<P & ErrorNotificationInterface> {
      static displayName = `withErrorNotification(${
        WrappedComponent.displayName || WrappedComponent.name || 'Component'
      })`;

      render() {
        const { error, ...props } = this.props;
        if (error) {
          return (
            <Notification
              type="error"
              message={_t(
                'Uh oh. Something unexpected happened. We recommend refreshing your browser. What happened as best we know: #{message}',
                error
              )}
            />
          );
        } else {
          // Working around spread props typing issues when we want to omit a prop.
          return <WrappedComponent {...(props as P)} />;
        }
      }
    };
  };

export default withErrorNotification;
